import { useAuditReportsListReportDefinition } from '@mi-front/api-v2';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ViewLoader } from '@ui/utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, RadioButtonGroup, SelectElement } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

export interface ReportExportDialogParams {
  format: string;
  definitionId: string;
}

const reportExportDialogSchema = z.object({
  format: z.string().min(1),
  definitionId: z.string().min(1),
});

interface Props {
  open: boolean;
  onClose: () => void;
  reportType: string;
  /** If provided (e.g. from report.data.export.options.definitionId), used as default selected definition when present in the definitions list. */
  initialDefinitionId?: string;
  onConfirm: (params: ReportExportDialogParams) => void | Promise<void>;
}

const defaultValues: ReportExportDialogParams = {
  format: 'xls',
  definitionId: '',
};

/**
 * Generic export dialog for report download. Lets the user choose format (XLS/CSV)
 * and definition (template), then calls onConfirm with both values.
 * Uses reportType to fetch definitions via useReportDefinitionList.
 */
export function ReportExportDialog({
  open,
  onClose,
  reportType,
  initialDefinitionId,
  onConfirm,
}: Props) {
  const definitionsQuery = useAuditReportsListReportDefinition({
    reportType,
  });

  // Prefer report's saved definition (report.data.export.options.definitionId) if it exists in the list; otherwise fall back to isDefault
  const defaultDefinitionId =
    (initialDefinitionId && definitionsQuery.data?.find((d) => d.id === initialDefinitionId)?.id) ||
    definitionsQuery.data?.find((d) => d.isDefault)?.id ||
    '';

  const formContext = useForm<ReportExportDialogParams>({
    defaultValues: {
      ...defaultValues,
      definitionId: defaultDefinitionId || '',
    },
    resolver: zodResolver(reportExportDialogSchema),
  });

  // Set default definition when definitions load (initialDefinitionId from report, or isDefault)
  useEffect(() => {
    if (defaultDefinitionId && definitionsQuery.data?.length) {
      formContext.reset({
        format: 'xls',
        definitionId: defaultDefinitionId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only set default when definitions load
  }, [defaultDefinitionId, definitionsQuery.data]);

  const handleSuccess = async (data: ReportExportDialogParams) => {
    await onConfirm(data);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="audit.reportsView.exportDialog.title"
          defaultMessage="Descargar reporte"
        />
      </DialogTitle>
      {definitionsQuery.isLoading ? (
        <DialogContent>
          <ViewLoader />
        </DialogContent>
      ) : (
        <FormContainer formContext={formContext} onSuccess={handleSuccess}>
          <DialogContent>
            <Stack spacing={3}>
              <Stack spacing={0}>
                <Typography variant="subtitle2" component={FormLabel}>
                  <FormattedMessage
                    id="audit.reportsView.exportDialog.format"
                    defaultMessage="Formato"
                  />
                </Typography>
                <RadioButtonGroup
                  name="format"
                  options={[
                    { id: 'xls', label: 'XLS' },
                    { id: 'csv', label: 'CSV' },
                  ]}
                  row
                />
              </Stack>

              {definitionsQuery.data && definitionsQuery.data.length > 0 ? (
                <SelectElement
                  name="definitionId"
                  label={
                    <FormattedMessage
                      id="audit.gipReport.export.definition"
                      defaultMessage="Plantilla"
                    />
                  }
                  options={definitionsQuery.data}
                  labelKey="name"
                  fullWidth
                  required
                />
              ) : (
                <FormattedMessage
                  id="audit.reportsView.exportDialog.noDefinitions"
                  defaultMessage="No hay plantillas disponibles"
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={onClose}>
              <FormattedMessage id="common.actions.cancel" defaultMessage="Cancelar" />
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!definitionsQuery.data?.length}
            >
              <FormattedMessage
                id="audit.reportsView.exportDialog.confirm"
                defaultMessage="Descargar"
              />
            </Button>
          </DialogActions>
        </FormContainer>
      )}
    </Dialog>
  );
}
